// shared/src/econ-calendar-param.ts
// Operator-curated econ-calendar source — the ECON_CALENDAR runtime_param.
//
// 2026-06-11 brief revision (docs/econ-calendar-param-2026-06-11-brief-revision.md).
// A JSON array of upcoming US macro events the operator edits on the Params tab
// (like ACCOUNT_ADDRESSES). api-worker's /api/econ-calendar route UNION+DEDUPs
// these with the FRED-sourced calendar (manual-wins), so the operator can:
//   · fix bad FRED dates (the trigger: FRED's "FOMC Press Release" release_id
//     101 forward-dated the Fed decision to 6/10; the real meeting is 6/17), and
//   · add events FRED's free release-dates feed omits.
//
// Shape is operator-friendly: { date, event } required + a few optional metadata
// fields. The chip row renders NAME + DATE only (it shows neither time nor
// forecast even for FRED events), so `time`/`forecast`/`previous` are kept as
// editor metadata and are NOT rendered — see the brief revision §"Chip content".
//
// NOT .strict() on the event object on purpose: this param lives inside the
// fleet-wide strict ParamsSchema, so a too-rigid inner schema would let a single
// stray operator field throw loadRuntimeParams for EVERY worker. date + event
// are validated; extra keys pass through harmlessly.

import { z } from 'zod';
import type { EconCalendarEvent } from './types/econ-calendar.js';

export const EconCalendarParamEventSchema = z.object({
  // YYYY-MM-DD release date (ET). Bucketing + dedup key with the event family.
  date:     z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'must be YYYY-MM-DD'),
  // Release name, e.g. "Fed Interest Rate Decision", "Core Inflation Rate MoM".
  // econEventCode() maps it to a marquee family + short chip label.
  event:    z.string().min(1),
  // Optional editor metadata (NOT rendered in the chip row). `time` keeps the
  // ET clock label (e.g. "02:00 PM") so the operator sees when each event fires;
  // forecast/previous accept opaque strings (TE units like "3.75%"/"172K").
  time:     z.string().optional(),
  forecast: z.string().optional(),
  previous: z.string().optional(),
});
export type EconCalendarParamEvent = z.infer<typeof EconCalendarParamEventSchema>;

// No `.min` — an empty array is valid and means "FRED-only" (the manual param
// contributes nothing). Matches the design: the calendar param is additive.
export const EconCalendarParamSchema = z.array(EconCalendarParamEventSchema);
export type EconCalendarParam = z.infer<typeof EconCalendarParamSchema>;

/**
 * Project an operator param event onto the wire EconCalendarEvent shape the chip
 * row consumes. ts/forecast/previous are null (the chip renders name+date only,
 * per the 2026-06-11 decision); impact is 'high' (the calendar surfaces only
 * high-impact macro). The optional metadata strings are intentionally dropped
 * here — they exist for the editor, not the renderer.
 */
export function econParamEventToCalendarEvent(p: EconCalendarParamEvent): EconCalendarEvent {
  return {
    ts:       null,
    date:     p.date,
    event:    p.event,
    impact:   'high',
    forecast: null,
    previous: null,
  };
}

// ─── LLM-chain injection (2026-06-11) ──────────────────────────────────────
// The operator-curated calendar also anchors the LLM's catalyst/thesis DATES,
// not just the dashboard chip row — see docs/econ-calendar-llm-injection-
// 2026-06-11-brief-revision.md. The macro stages (m.synthesis + the two
// m-analysts) previously invented forward catalyst dates from training memory
// (the same failure class that put a bogus FOMC on the chip row). These two
// helpers are PURE (no I/O) so each chain stage can call them with
// params.ECON_CALENDAR + ctx.today_iso.

/**
 * Default forward horizon for the LLM block — 12 weeks (84d), matching
 * m-synthesis's "≤12w forward catalyst ≥ TODAY" catalyst-date rule. A constant,
 * not a runtime_param: the window only bounds which curated events are shown,
 * and the synthesis rule already caps the catalyst at 12w.
 */
export const ECON_LLM_LOOKAHEAD_DAYS = 84;

/**
 * Select operator-curated events in [todayIso, todayIso+horizonDays], sorted by
 * date ascending, projected onto the wire EconCalendarEvent shape. PURE +
 * deterministic (UTC-midnight date compare). Events whose `date` is malformed
 * are dropped; a malformed `todayIso` yields [] (no block rendered). Returns []
 * when the param is empty/absent — the FRED-only / no-curation path.
 */
export function selectForwardEconParamEvents(
  param:       EconCalendarParam | undefined,
  todayIso:    string,
  horizonDays: number = ECON_LLM_LOOKAHEAD_DAYS,
): EconCalendarEvent[] {
  if (!param || param.length === 0) return [];
  const todayMs = Date.parse(`${todayIso}T00:00:00Z`);
  if (!Number.isFinite(todayMs)) return [];
  const horizonMs = todayMs + horizonDays * 86_400_000;
  return param
    .filter((p) => {
      const ms = Date.parse(`${p.date}T00:00:00Z`);
      return Number.isFinite(ms) && ms >= todayMs && ms <= horizonMs;
    })
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(econParamEventToCalendarEvent);
}

/**
 * Render the forward calendar as a prompt block for the macro LLM stages.
 * Operator-curated → AUTHORITATIVE for any catalyst date the stage emits.
 * Returns '' for an empty list (caller omits the block entirely). The trailing
 * newline lets callers concatenate without managing spacing (mirrors
 * renderWorldStateGrounding).
 */
export function renderUpcomingEconBlock(events: ReadonlyArray<EconCalendarEvent>): string {
  if (events.length === 0) return '';
  const lines = events.map((e) => `  ${e.date}  ${e.event}`).join('\n');
  return (
    'UPCOMING ECON CALENDAR (operator-curated, forward-dated — AUTHORITATIVE for any catalyst date; '
    + 'use these EXACT dates, do not invent or shift a macro event to a different day):\n'
    + lines
    + '\n'
  );
}

// ─── Operator paste → param entries (2026-07-05) ─────────────────────────────
// The operator pastes a Trading Economics US-calendar block into the Params-tab
// ECON_CALENDAR "Paste calendar" popup; these PURE helpers parse it into param
// entries + merge/dedup against the current param, so the operator no longer
// hand-converts the block (or asks a session to do it) each month.
//
// Paste shape — columns are Actual | Previous | Consensus | Forecast (tab- or
// multi-space-delimited), one block per day:
//   Monday August 03 2026    Actual    Previous    Consensus    Forecast
//   10:00 AM
//   US
//   ISM Manufacturing PMI JUL        53.3
//   Tuesday August 04 2026    Actual    Previous    Consensus    Forecast
//   ...
// A day-header line sets the current date; a time line sets the clock; a country
// line (e.g. "US") is skipped; the next non-structural line is the event —
// "<name>  <column values…>". Event NAMES are normalized to the seed convention
// (trailing reference-period tokens stripped — "ISM Manufacturing PMI JUL" →
// "ISM Manufacturing PMI"; MoM/YoY/QoQ are KEPT, they name distinct releases).

const ECON_MONTHS: Readonly<Record<string, number>> = {
  january: 1, february: 2, march: 3, april: 4, may: 5, june: 6, july: 7,
  august: 8, september: 9, october: 10, november: 11, december: 12,
  jan: 1, feb: 2, mar: 3, apr: 4, jun: 6, jul: 7, aug: 8,
  sep: 9, sept: 9, oct: 10, nov: 11, dec: 12,
};

const ECON_WEEKDAYS: ReadonlySet<string> = new Set([
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  'mon', 'tue', 'tues', 'wed', 'thu', 'thur', 'thurs', 'fri', 'sat', 'sun',
]);

// Trailing tokens that name a reference period / estimate vintage, not the
// release itself — stripped from the event name (repeatedly, from the end).
// Month names/abbrevs, quarters (Q1–Q4), a 4-digit year, and vintage qualifiers
// (Prel/Final/Adv/Flash/Est + 1st/2nd/3rd). MoM/YoY/QoQ are intentionally absent.
const ECON_PERIOD_SUFFIX =
  /^(?:jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec|january|february|march|april|june|july|august|september|october|november|december|q[1-4]|\d{4}|prel|preliminary|final|adv|advance|flash|est|1st|2nd|3rd)$/i;

const ECON_TIME_RE  = /^(\d{1,2}):(\d{2})\s*(am|pm)$/i;
const ECON_COUNTRY_RE = /^[A-Z]{2,3}$/; // "US", "EA", "GBR" — a bare country code line.

/** Strip trailing reference-period tokens ("… JUL", "… 2nd Est Q2") from an
 *  event name, matching the seed convention. Never empties the name. Exported
 *  for the paste preview + unit tests. */
export function stripEconEventPeriodSuffix(name: string): string {
  const toks = name.trim().split(/\s+/).filter(Boolean);
  while (toks.length > 1 && ECON_PERIOD_SUFFIX.test(toks[toks.length - 1]!)) toks.pop();
  return toks.join(' ');
}

/** Dedup key for a param event — date + case/space-normalized event name. */
export function econParamDedupKey(e: { date: string; event: string }): string {
  return `${e.date}|${e.event.trim().toLowerCase().replace(/\s+/g, ' ')}`;
}

function econHeaderToIso(month: string, day: string, year: string): string | null {
  const m = ECON_MONTHS[month.toLowerCase()];
  if (!m) return null;
  const d = Number.parseInt(day, 10);
  if (!Number.isFinite(d) || d < 1 || d > 31) return null;
  return `${year}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function normalizeEconTime(raw: string): string | undefined {
  const m = ECON_TIME_RE.exec(raw.trim());
  if (!m) return undefined;
  return `${m[1]!.padStart(2, '0')}:${m[2]} ${m[3]!.toUpperCase()}`;
}

// Split an event line into name + best-effort forecast/previous. Real clipboard
// rows are TAB-delimited (columns [name, Actual, Previous, Consensus, Forecast])
// → mapped POSITIONALLY (correct). A space-collapsed paste (no tabs) falls back
// to splitting on runs of 2+ spaces + a heuristic (previous = first value,
// forecast = last when ≥2). These are editor-only metadata (NOT rendered), so a
// mislabel in the space-only fallback is cosmetic.
function parseEconEventLine(line: string): { name: string; previous?: string; forecast?: string } {
  let name: string;
  let previous: string | undefined;
  let forecast: string | undefined;
  if (line.includes('\t')) {
    // Strip leading indentation FIRST: TE rows paste with a leading tab/space
    // indent, so a raw split('\t') would make cells[0] the empty indent cell
    // (→ empty name → whole row wrongly skipped). Trimming the left edge aligns
    // cells positionally to [name, Actual, Previous, Consensus, Forecast].
    const cells = line.replace(/^\s+/, '').split('\t').map((c) => c.trim());
    name     = cells[0] ?? '';
    previous = cells[2] || undefined;
    forecast = cells[4] || undefined;
  } else {
    const parts = line.trim().split(/\s{2,}/).map((s) => s.trim()).filter(Boolean);
    const cols = parts.slice(1);
    name     = parts[0] ?? '';
    previous = cols[0] || undefined;
    forecast = cols.length > 1 ? cols[cols.length - 1] : undefined;
  }
  const out: { name: string; previous?: string; forecast?: string } = { name };
  if (previous) out.previous = previous;
  if (forecast) out.forecast = forecast;
  return out;
}

export interface EconCalendarPasteResult {
  readonly events:   EconCalendarParamEvent[]; // parsed + name-normalized
  readonly warnings: string[];                 // non-fatal skips (bad date, orphan event…)
}

/**
 * Parse a pasted Trading-Economics-style US calendar block into param events.
 * PURE + deterministic. Skips (with a warning) events that appear before any
 * day-header or whose header date is malformed; unrecognized structural lines
 * are treated as events only when a date is in scope.
 */
export function parseEconCalendarPaste(text: string): EconCalendarPasteResult {
  const events: EconCalendarParamEvent[] = [];
  const warnings: string[] = [];
  let curDate: string | null = null;
  let curTime: string | undefined;

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.replace(/\s+$/, ''); // keep internal tabs; drop trailing ws
    const t = line.trim();
    if (!t) continue;

    // Day header: "<weekday> <Month> <day> <year> [Actual Previous …]"
    const hdr = /^([A-Za-z]+)\s+([A-Za-z]+)\s+(\d{1,2})\s+(\d{4})\b/.exec(t);
    if (hdr && ECON_WEEKDAYS.has(hdr[1]!.toLowerCase()) && ECON_MONTHS[hdr[2]!.toLowerCase()]) {
      const iso = econHeaderToIso(hdr[2]!, hdr[3]!, hdr[4]!);
      if (iso) { curDate = iso; curTime = undefined; }
      else { warnings.push(`Unparseable date header: "${t}"`); }
      continue;
    }

    // Time line ("10:00 AM").
    const time = normalizeEconTime(t);
    if (time) { curTime = time; continue; }

    // Bare country-code line ("US") — skip.
    if (ECON_COUNTRY_RE.test(t)) continue;

    // Skip the odd repeated column-header fragment if it appears standalone.
    if (/^(actual|previous|consensus|forecast)$/i.test(t)) continue;

    // Otherwise: an event line.
    if (!curDate) { warnings.push(`Event before any date, skipped: "${t}"`); continue; }
    const { name, previous, forecast } = parseEconEventLine(line);
    const event = stripEconEventPeriodSuffix(name);
    if (!event) { warnings.push(`Empty event name, skipped: "${t}"`); continue; }

    const entry: EconCalendarParamEvent = { date: curDate, event };
    if (curTime)  entry.time = curTime;
    if (previous) entry.previous = previous;
    if (forecast) entry.forecast = forecast;
    events.push(entry);
    curTime = undefined; // don't let a time bleed onto a following time-less event
  }

  return { events, warnings };
}

export interface EconCalendarMergeResult {
  readonly merged:    EconCalendarParamEvent[]; // full param, deduped + date-sorted
  readonly added:     EconCalendarParamEvent[]; // events new to the param
  readonly addedCount:     number;
  readonly updatedCount:   number; // existing entries that gained missing metadata
  readonly unchangedCount: number; // pasted events already fully present
}

/**
 * Merge parsed paste events into the existing param. Dedup key = date + name
 * (case/space-insensitive). Existing date/event win; only MISSING metadata
 * (time/forecast/previous) is filled in from the paste — never overwritten.
 * Result is sorted by date, then event. PURE.
 */
export function mergeEconCalendarParam(
  existing: ReadonlyArray<EconCalendarParamEvent>,
  parsed:   ReadonlyArray<EconCalendarParamEvent>,
): EconCalendarMergeResult {
  const byKey = new Map<string, EconCalendarParamEvent>();
  for (const e of existing) byKey.set(econParamDedupKey(e), { ...e });

  const added: EconCalendarParamEvent[] = [];
  let updatedCount = 0;
  let unchangedCount = 0;

  for (const p of parsed) {
    const key = econParamDedupKey(p);
    const cur = byKey.get(key);
    if (!cur) { byKey.set(key, { ...p }); added.push({ ...p }); continue; }

    const next = { ...cur };
    let changed = false;
    for (const f of ['time', 'forecast', 'previous'] as const) {
      const has = next[f] != null && next[f] !== '';
      const inc = p[f] != null && p[f] !== '';
      if (!has && inc) { next[f] = p[f]; changed = true; }
    }
    if (changed) { byKey.set(key, next); updatedCount++; }
    else unchangedCount++;
  }

  const merged = [...byKey.values()].sort(
    (a, b) => a.date.localeCompare(b.date) || a.event.localeCompare(b.event),
  );
  return { merged, added, addedCount: added.length, updatedCount, unchangedCount };
}
